import { IDP_PROVIDER, IDP_PROTOCOL } from "./idpProviders";
import { getProviderDefinition } from "./providerRegistry";
import { resolveUnknownProvider } from "./normalizeSsoProvider";

/**
 * @typedef {object} ResolvedProviderBranding
 * @property {import('./idpProviders').IdpProviderId} provider
 * @property {'oidc' | 'saml'} protocol
 * @property {string} buttonLabel
 * @property {string | null} logoUrl
 * @property {string | null} brandColor
 * @property {string | null} buttonBackground
 * @property {string | null} buttonTextColor
 * @property {string} iconKey
 * @property {string} loadingMessage
 * @property {string} ariaLabel
 */

/**
 * Relative luminance for WCAG contrast decisions (sRGB).
 * @param {string} hex
 * @returns {number}
 */
function relativeLuminance(hex) {
  const normalized = hex.replace("#", "");
  if (normalized.length !== 6) return 0.5;

  const channels = [0, 2, 4].map((offset) => {
    const value = parseInt(normalized.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/**
 * @param {string | null | undefined} background
 * @param {string | null | undefined} preferred
 * @returns {string}
 */
function pickReadableTextColor(background, preferred) {
  if (preferred) return preferred;
  if (!background) return "#1B1B1B";
  return relativeLuminance(background) > 0.45 ? "#1B1B1B" : "#FFFFFF";
}

/**
 * Resolve final SSO button branding.
 * Priority: tenant overrides → known provider defaults → generic fallback.
 *
 * @param {object} input
 * @param {string} [input.provider]
 * @param {'oidc' | 'saml'} [input.protocol]
 * @param {string} [input.displayName]
 * @param {string | null} [input.buttonLabel]
 * @param {string | null} [input.logoUrl]
 * @param {string | null} [input.brandColor]
 * @returns {ResolvedProviderBranding}
 */
export function resolveProviderBranding(input = {}) {
  const protocol =
    input.protocol === IDP_PROTOCOL.SAML
      ? IDP_PROTOCOL.SAML
      : IDP_PROTOCOL.OIDC;

  const hasTenantBranding = Boolean(
    input.buttonLabel || input.logoUrl || input.brandColor
  );

  let providerKey = resolveUnknownProvider(input.provider, protocol);

  if (hasTenantBranding && providerKey === IDP_PROVIDER.GENERIC_OIDC) {
    providerKey = IDP_PROVIDER.CUSTOM;
  }

  const registryEntry =
    getProviderDefinition(providerKey) ||
    getProviderDefinition(
      protocol === IDP_PROTOCOL.SAML
        ? IDP_PROVIDER.GENERIC_SAML
        : IDP_PROVIDER.GENERIC_OIDC
    );

  const displayName = String(
    input.displayName || registryEntry?.displayName || "Identity Provider"
  ).trim();

  const buttonLabel =
    (input.buttonLabel && String(input.buttonLabel).trim()) ||
    registryEntry?.defaultButtonLabel ||
    (protocol === IDP_PROTOCOL.SAML
      ? "Continue with Corporate SSO"
      : "Continue with Single Sign-On");

  const logoUrl = input.logoUrl ? String(input.logoUrl).trim() : null;
  const brandColor = input.brandColor
    ? String(input.brandColor).trim()
    : registryEntry?.brandColor || null;

  const buttonBackground =
    input.brandColor?.trim() ||
    registryEntry?.buttonBackground ||
    (protocol === IDP_PROTOCOL.SAML || providerKey === IDP_PROVIDER.GENERIC_OIDC
      ? null
      : brandColor);

  const buttonTextColor = pickReadableTextColor(
    buttonBackground,
    registryEntry?.buttonTextColor
  );

  const iconKey = registryEntry?.iconKey || "shield";

  const loadingTarget =
    input.buttonLabel || displayName !== registryEntry?.displayName
      ? displayName
      : registryEntry?.displayName || "Identity Provider";

  return {
    provider: providerKey,
    protocol,
    buttonLabel,
    logoUrl,
    brandColor,
    buttonBackground,
    buttonTextColor,
    iconKey,
    loadingMessage: `Connecting to ${loadingTarget}…`,
    ariaLabel: buttonLabel,
  };
}
